import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ProductDetails.css';
import { dbData } from './data';
import StickyBuyBar from './components/StickyBuyBar';
import { StarIcon, SparklesIcon, DropletIcon, ZapIcon, FlaskIcon, CartIcon, CheckIcon, ShieldCheckIcon } from './Icons';

const VARIANTS = [
    { id: 1, name: 'Blue', color: '#1a56db', productKey: 'blue', arName: 'أزرق' },
    { id: 3, name: 'Red', color: '#e53935', productKey: 'red', arName: 'أحمر' },
];

function DetailCard({ icon, title, children }) {
    return (
        <div className="detail-card">
            <div className="detail-card-header">
                <span className="detail-card-icon">{icon}</span>
                <span className="detail-card-title">{title}</span>
            </div>
            <div className="detail-card-body">{children}</div>
        </div>
    );
}

function ProductDetails({ setPage, activeKey, setActiveKey }) {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language === 'ar' ? 'ar' : (i18n.language === 'fr' ? 'fr' : 'en');

    const [allProducts, setAllProducts] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [showSticky, setShowSticky] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
    const buyDockRef = React.useRef(null);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 900;
            setIsMobile(mobile);
        };
        handleResize(); // Initial check
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setAllProducts(dbData.products);
        setTimeout(() => setIsLoaded(true), 100);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (buyDockRef.current) {
                const rect = buyDockRef.current.getBoundingClientRect();
                const shouldShow = rect.top > window.innerHeight || rect.bottom < 0;
                setShowSticky(shouldShow);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        // Initial check after a short delay for rendering
        const t = setTimeout(handleScroll, 500);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(t);
        };
    }, [isLoaded, activeKey]);

    useEffect(() => {
        setImgLoaded(false);
        const t = setTimeout(() => setImgLoaded(true), 200);
        return () => clearTimeout(t);
    }, [activeKey]);

    // Sync body class with sticky visibility
    useEffect(() => {
        if (showSticky) {
            document.body.classList.add('sticky-bar-visible');
        } else {
            document.body.classList.remove('sticky-bar-visible');
        }
        return () => document.body.classList.remove('sticky-bar-visible');
    }, [showSticky]);

    if (!allProducts) return (
        <div className="details-loading">
            <div className="loading-ring"></div>
            <p>{t('details.loading')}</p>
        </div>
    );

    const product = allProducts[activeKey];
    const content = product[currentLang] || product['en'];
    const activeVariant = VARIANTS.find(v => v.productKey === activeKey);

    return (
        <>
            <div className={`details-container ${isLoaded ? 'loaded' : ''}`}>
                {/* Back button */}
            <button className="details-back-btn" onClick={() => setPage('home')}>
                {t('details.back')}
            </button>

            {/* Hero strip */}
            <div className="details-hero" style={{ '--variant-color': activeVariant?.color }}>
                {/* Left: Image */}
                <div className="details-image-col">
                    <div className="details-image-stage">
                        <img
                            src={isMobile ? (product.packImage || product.image) : product.image}
                            alt={content.name}
                            className={`details-product-img ${imgLoaded ? 'visible' : ''}`}
                        />
                    </div>
                </div>

                {/* Right: Header info */}
                <div className="details-header-info">
                    {(currentLang === 'ar' || currentLang === 'fr') && <span className="details-brand">{content.type} · {content.brand}</span>}
                    <h1 className="reveal-text"><span>{content.name}</span></h1>

                    <div className="details-stars">
                        <span className="stars-row">
                            <StarIcon /><StarIcon /><StarIcon /><StarIcon /><StarIcon />
                        </span>
                        <span>{t('details.rating')}</span>
                    </div>

                    <div className="details-price-row">
                        <span className="d-price">{product.price} <small>{t('common.mad')}</small></span>
                        <span className="d-old-price">{product.oldPrice} {t('common.mad')}</span>
                        <span className="d-discount">
                            -{Math.round((1 - product.price / product.oldPrice) * 100)}%
                        </span>
                    </div>

                    {/* Variant selector */}
                    <div className="details-variant-section">
                        <span className="variant-label">{t('details.variant')}:</span>
                        <div className="details-color-selector">
                            {VARIANTS.map(v => (
                                <button
                                    key={v.id}
                                    className={`d-color-circle ${activeKey === v.productKey ? 'active' : ''}`}
                                    style={{ backgroundColor: v.color }}
                                    onClick={() => setActiveKey(v.productKey)}
                                    title={i18n.language === 'ar' ? (v.arName || v.name) : v.name}
                                >
                                    {activeKey === v.productKey && <span><CheckIcon /></span>}
                                </button>
                            ))}
                        </div>
                        <span className="active-variant-name">
                            {t(`common.variants.${activeKey}`)}
                        </span>
                    </div>

                    {/* Quick specs */}
                    <div className="quick-specs">
                        <div className="quick-spec"><span>{t('details.volume')}</span><strong>{content.size_ml}{t('common.units.ml')}</strong></div>
                        <div className="quick-spec"><span>{t('details.hairType')}</span><strong>{content.hair_type}</strong></div>
                        <div className="quick-spec"><span>{t('details.usage')}</span><strong>{content.usage}</strong></div>
                        <div className="quick-spec"><span>{t('details.brand')}</span><strong>{content.brand}</strong></div>
                    </div>

                    {/* Detail cards — before the buy button */}
                    <div className="details-cards-grid inline">
                        <DetailCard icon={<SparklesIcon />} title={t('details.benefits')}>
                            <ul className="detail-list">
                                {(content.benefits || []).map((b, i) => (
                                    <li key={i}><span className="list-dot benefit-dot"></span>{b}</li>
                                ))}
                            </ul>
                        </DetailCard>

                        <DetailCard icon={<FlaskIcon />} title={t('details.ingredients')}>
                            <ul className="detail-list">
                                {(content.ingredients || []).map((ing, i) => (
                                    <li key={i}><span className="list-dot ingredient-dot"></span>{ing}</li>
                                ))}
                            </ul>
                        </DetailCard>

                        <DetailCard icon={<ZapIcon />} title={t('details.freeFrom')}>
                            <ul className="detail-list">
                                {(content.free_from || []).map((f, i) => (
                                    <li key={i}><span className="list-dot free-dot"></span>{f}</li>
                                ))}
                            </ul>
                        </DetailCard>

                        <DetailCard icon={<DropletIcon />} title={t('details.description')}>
                            <p className="detail-description">{content.description}</p>
                        </DetailCard>
                    </div>

                    <div className="details-buy-wrapper" ref={buyDockRef}>
                        <button className="details-buy-btn shine-button" onClick={() => setPage('buy')}>
                            <CartIcon /> {t('details.buyNow')}
                        </button>
                    </div>
                </div>
            </div>
        </div>

            {/* Sticky Mobile Buy Bar */}
            <StickyBuyBar 
                visible={showSticky} 
                onClick={() => setPage('buy')} 
            />
        </>
    );
}

export default ProductDetails;
